import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Crown, Check, XCircle, RefreshCw, Calendar } from "lucide-react";

const PLAN_LABEL = { parent: "Parent Full Access", teacher: "Teacher Classroom", school: "School & District", reseller: "Reseller Partner" };

export default function ManageSubscription() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [sub, setSub] = useState(undefined);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, p] = await Promise.all([api.mySub(), api.getPlans()]);
    setSub(s.subscription || null);
    setPlans(p.plans);
  };
  useEffect(() => { load(); }, []);

  if (sub === undefined) return <div className="p-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;

  if (!sub || sub.status !== "active") {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <Crown size={40} className="mx-auto text-[#F59E0B]" />
        <h1 className="mt-4 font-heading text-2xl font-extrabold text-[#1F2937]">No active subscription</h1>
        <p className="mt-2 font-body text-[#6B7280]">Upgrade to unlock the full PreK–8 curriculum and progress reports.</p>
        <button onClick={() => navigate("/pricing")} data-testid="ms-see-plans" className="mt-6 rounded-full bg-[#0D9488] px-6 py-3 font-heading font-bold text-white hover:bg-[#0B7F74]">See plans</button>
      </div>
    );
  }

  const doChange = async (lookup_key) => {
    setBusy(true);
    try { const r = await api.changeSub(lookup_key); await refreshUser(); await load(); toast.success(`Switched to ${PLAN_LABEL[r.plan]} (${r.interval}ly)`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const doCancel = async () => {
    setBusy(true);
    try { await api.cancelSub(); await refreshUser(); await load(); toast.success("Subscription will end at the period close."); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const doResume = async () => {
    setBusy(true);
    try { await api.resumeSub(); await refreshUser(); await load(); toast.success("Subscription resumed."); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const currentPlanObj = plans.find((p) => p.plan === sub.plan);
  const otherInterval = sub.interval === "month" ? "year" : "month";
  const switchIntervalKey = currentPlanObj?.prices.find((x) => x.interval === otherInterval)?.lookup_key;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-[#1F2937]">Manage Subscription</h1>

      <div className="mt-6 rounded-3xl border-2 border-[#0D9488] bg-white p-6" data-testid="ms-current-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Current plan</div>
            <div className="mt-1 font-heading text-2xl font-extrabold text-[#1F2937]" data-testid="ms-plan-name">{PLAN_LABEL[sub.plan] || sub.plan}</div>
            <div className="mt-1 font-body text-sm text-[#6B7280] flex items-center gap-1"><Calendar size={14} /> Billed {sub.interval === "year" ? "annually" : "monthly"}</div>
          </div>
          <span className="rounded-full bg-[#10B981]/15 px-3 py-1 font-heading text-sm font-bold text-[#047857]"><Check size={14} className="inline" /> Active</span>
        </div>
        {sub.cancel_at_period_end && (
          <div className="mt-4 flex items-center justify-between rounded-2xl bg-[#F59E0B]/10 px-4 py-3">
            <span className="font-body text-sm font-semibold text-[#B45309]">Ends at the current period. Access stays until then.</span>
            <button onClick={doResume} disabled={busy} data-testid="ms-resume" className="rounded-full bg-[#10B981] px-4 py-1.5 font-heading text-sm font-bold text-white">Resume</button>
          </div>
        )}
      </div>

      {/* Switch billing period */}
      {switchIntervalKey && (
        <div className="mt-5 flex items-center justify-between rounded-3xl border border-[#E5DECF] bg-white p-5">
          <div>
            <div className="font-heading font-bold text-[#1F2937]">Switch to {otherInterval === "year" ? "annual" : "monthly"} billing</div>
            <p className="font-body text-sm text-[#6B7280]">{otherInterval === "year" ? "Save 20% by paying yearly." : "Switch back to paying monthly."}</p>
          </div>
          <button onClick={() => doChange(switchIntervalKey)} disabled={busy} data-testid="ms-switch-interval" className="inline-flex items-center gap-2 rounded-full bg-[#0D9488] px-5 py-2.5 font-heading font-bold text-white hover:bg-[#0B7F74]">
            <RefreshCw size={16} /> Switch
          </button>
        </div>
      )}

      {/* Change plan tier */}
      <h2 className="mt-8 font-heading text-lg font-bold text-[#1F2937]">Change plan</h2>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        {plans.filter((p) => p.plan !== "reseller").map((p) => {
          const price = p.prices.find((x) => x.interval === sub.interval) || p.prices[0];
          const isCurrent = p.plan === sub.plan;
          return (
            <div key={p.plan} className={`rounded-3xl border-2 bg-white p-5 ${isCurrent ? "border-[#10B981]" : "border-[#E5DECF]"}`} data-testid={`ms-plan-${p.plan}`}>
              <div className="font-heading text-lg font-bold text-[#1F2937]">{p.name}</div>
              <div className="font-body text-sm text-[#6B7280]">{p.tagline}</div>
              <div className="mt-2 font-heading text-xl font-extrabold text-[#1F2937]">{price.label}</div>
              <button onClick={() => doChange(price.lookup_key)} disabled={busy || isCurrent} data-testid={`ms-change-${p.plan}`}
                className={`mt-3 w-full rounded-full px-4 py-2 font-heading font-bold ${isCurrent ? "bg-[#10B981]/15 text-[#047857] cursor-default" : "bg-[#1F2937] text-white hover:bg-black"}`}>
                {isCurrent ? "Current" : "Switch to this"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Cancel */}
      {!sub.cancel_at_period_end && (
        <button onClick={doCancel} disabled={busy} data-testid="ms-cancel" className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-[#E05A47] px-5 py-2.5 font-heading font-bold text-[#E05A47] hover:bg-[#E05A47]/5">
          <XCircle size={16} /> Cancel subscription
        </button>
      )}
    </div>
  );
}
